import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ArrowRightIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  PlayIcon,
  PlusSmallIcon,
  MinusSmallIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PricingSection from "../../components/shared/PricingSection";

type ToolKey =
  | "flippingTool"
  | "dropshipTool"
  | "realmStats"
  | "tsmListGenerator"
  | "snipeAlerts"
  | "inventoryAnalysis";

const TOOLS: { key: ToolKey; videoId: string; featureCount: number }[] = [
  { key: "flippingTool",      videoId: "1134850920", featureCount: 6 },
  { key: "dropshipTool",      videoId: "1134853063", featureCount: 6 },
  { key: "snipeAlerts",       videoId: "1173553017", featureCount: 3 },
  { key: "realmStats",        videoId: "1134845594", featureCount: 2 },
  { key: "tsmListGenerator",  videoId: "1134852243", featureCount: 3 },
  { key: "inventoryAnalysis", videoId: "1173612100", featureCount: 2 },
];

const Home = () => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const [openVideo, setOpenVideo] = useState<ToolKey | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | Window | null>(null);

  // The app layout scrolls inside <main>, not window — find that container
  // so the back-to-top button reads and writes the right scroll position.
  useEffect(() => {
    const scroller: HTMLElement | Window =
      rootRef.current?.closest("main") ?? window;
    scrollerRef.current = scroller;
    const getY = () =>
      scroller instanceof Window ? scroller.scrollY : scroller.scrollTop;
    const onScroll = () => setShowBackToTop(getY() > 600);
    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const goRegister = () => {
    navigate("/register");
    window.scrollTo(0, 0);
  };

  const activeTool = openVideo ? TOOLS.find((tt) => tt.key === openVideo) : null;
  const faqKeys = [0, 1, 2, 3, 4] as const;
  const edgeKeys = [0, 1, 2] as const;

  return (
    <div ref={rootRef} className="relative isolate overflow-hidden bg-fp-bg text-gray-100 font-sans antialiased">
      {/* One soft, static gold wash near the top — no motion, no tiling. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 -top-70 -z-10 h-140 w-225 -translate-x-1/2 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(234,179,8,0.09), transparent 65%)",
        }}
      />

      {/* ─────────────────────── HERO ─────────────────────── */}
      <section className="relative mx-auto max-w-3xl px-6 pt-8 pb-8 text-center sm:pt-10 lg:px-8 lg:pt-12 lg:pb-10">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
          <Trans
            ns="home"
            i18nKey="hero.title"
            components={{
              em: <em className="not-italic text-amber-300" />,
              br: <br className="hidden sm:inline" />,
            }}
          />
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-gray-400">
          {t("hero.subtitle")}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={goRegister}
            className="inline-flex items-center gap-2 rounded-sm bg-amber-400 px-5 py-2.5 text-sm font-medium text-stone-950 hover:bg-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200"
          >
            {t("hero.ctaPrimary")}
            <ArrowRightIcon className="size-4" />
          </button>
        </div>

        <dl className="mx-auto mt-6 flex max-w-md flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-500">
          {(["regions", "updated"] as const).map((k) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className="uppercase tracking-[0.14em]">{t(`hero.stats.${k}`)}:</span>
              <span className="font-medium text-white">{t(`hero.stats.${k}Value`)}</span>
            </div>
          ))}
        </dl>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <div className="flex justify-center py-4">
            <div className="w-87.5 h-57.5 overflow-hidden rounded-3xl drop-shadow-[0_0px_8px_rgba(37,99,235,0.5)] sm:w-190 sm:h-105 xl:w-230 xl:h-132.5 2xl:w-287.5 2xl:h-165">
              <iframe
                src="https://player.vimeo.com/video/1195151021?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                className="h-full w-full"
                title={t('hero.videoTitle')}
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js" defer></script>
          </div>
        </div>
      </section>

      {/* ─────────────────────── TOOLS ─────────────────────── */}
      <section
        id="tools"
        aria-label="Tools"
        className="relative scroll-mt-8 border-t border-white/10"
      >
        <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8 lg:py-12">
          <SectionHeader label={t("toolsSection.label")} title={t("toolsSection.sectionTitle")} />

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TOOLS.map(({ key }) => (
              <button
                key={key}
                onClick={() => setOpenVideo(key)}
                className="group relative flex flex-col rounded-sm border border-white/10 bg-white/[0.02] p-4 text-left hover:border-white/20 hover:bg-white/[0.04]"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-base font-semibold leading-tight text-white">
                    {t(`tools.${key}.title`)}
                  </h3>
                  <PlayIcon className="size-4 shrink-0 text-gray-600 group-hover:text-gray-400" />
                </div>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-gray-400">
                  {t(`tools.${key}.description`)}
                </p>
                <span className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-gray-500 group-hover:text-white">
                  {t("toolsSection.watchDemo")}
                  <ArrowRightIcon className="size-3" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────── THE EDGE ─────────────────────── */}
      <section className="relative border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-16">
          <SectionHeader label={t("edge.label")} title={t("edge.sectionTitle")} />
          <dl className="mt-10 grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-3">
            {edgeKeys.map((k) => (
              <div key={k}>
                <dt className="text-lg font-semibold text-white">
                  {t(`edge.items.${k}.title`)}
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-gray-400">
                  {t(`edge.items.${k}.body`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ─────────────────────── REVIEWS ─────────────────────── */}
      <section
        id="reviews"
        aria-label="Reviews"
        className="relative scroll-mt-8 border-t border-white/10"
      >
        <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8 lg:py-16">
          <SectionHeader label={t("reviews.label")} title={t("reviews.sectionTitle")} />
          <div className="mt-8 flex justify-center">
            <div className="relative aspect-video w-full max-w-4xl overflow-hidden rounded-3xl drop-shadow-[0_0px_8px_rgba(37,99,235,0.5)]">
              <iframe
                src="https://player.vimeo.com/video/1195731051?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                className="absolute inset-0 h-full w-full"
                title={t("reviews.videoTitle")}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────── PRICING ─────────────────────── */}
      <section
        id="pricing"
        aria-label="Pricing"
        className="relative scroll-mt-8 border-t border-white/10"
      >
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-16">
          <SectionHeader label={t("pricing.label")} title={t("pricing.sectionTitle")} subtitle={t("pricing.subtitle")} />
          <div className="mt-8">
            <PricingSection showUserInfo={false} />
          </div>
        </div>
      </section>

      {/* ─────────────────────── FAQ ─────────────────────── */}
      <section
        id="faq"
        aria-label="FAQ"
        className="relative scroll-mt-8 border-t border-white/10"
      >
        <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8 lg:py-16">
          <SectionHeader label={t("faq.label")} title={t("faq.sectionTitle")} />
          <dl className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {faqKeys.map((i) => (
              <Disclosure key={i} as="div" className="py-6">
                {({ open }) => (
                  <>
                    <dt>
                      <DisclosureButton className="group flex w-full items-start justify-between gap-6 text-left">
                        <span className="text-base font-medium text-white sm:text-lg">
                          {t(`faq.items.${i}.question`)}
                        </span>
                        <span className="mt-1 inline-flex size-6 shrink-0 items-center justify-center text-gray-400">
                          {open ? (
                            <MinusSmallIcon className="size-4" />
                          ) : (
                            <PlusSmallIcon className="size-4" />
                          )}
                        </span>
                      </DisclosureButton>
                    </dt>
                    <DisclosurePanel as="dd" className="mt-3 pr-10">
                      <p className="text-sm leading-relaxed text-gray-400">
                        {t(`faq.items.${i}.answer`)}
                      </p>
                    </DisclosurePanel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </section>

      {/* ─────────────────────── VIDEO MODAL ─────────────────────── */}
      <Dialog open={!!openVideo} onClose={() => setOpenVideo(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-8">
          <DialogPanel className="relative w-full max-w-5xl overflow-hidden rounded-sm border border-white/10 bg-fp-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gray-500">
                  {activeTool ? t(`toolsSection.tags.${activeTool.key}`) : ""}
                </span>
                <span className="text-white/30">·</span>
                <span className="text-base text-white">
                  {activeTool ? t(`tools.${activeTool.key}.title`) : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpenVideo(null)}
                className="rounded-full p-1.5 text-gray-400 transition hover:bg-white/10 hover:text-white"
                aria-label={t("modal.close")}
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            <div className="relative aspect-video bg-black">
              {activeTool && (
                <iframe
                  key={activeTool.key}
                  src={`https://player.vimeo.com/video/${activeTool.videoId}?badge=0&autoplay=1&muted=1&autopause=0&player_id=0&app_id=58479`}
                  className="absolute inset-0 h-full w-full"
                  frameBorder={0}
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  title={t(`tools.${activeTool.key}.videoTitle`)}
                />
              )}
            </div>
            {activeTool && (
              <div className="border-t border-white/10 px-5 py-5">
                <ul className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                  {Array.from({ length: activeTool.featureCount }).map((_, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-300" />
                      <span>{t(`tools.${activeTool.key}.features.${i}`)}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    setOpenVideo(null);
                    goRegister();
                  }}
                  className="mt-5 inline-flex items-center gap-2 rounded-sm bg-amber-400 px-5 py-2.5 text-sm font-medium text-stone-950 transition hover:bg-amber-300"
                >
                  {t("hero.ctaPrimary")}
                  <ArrowRightIcon className="size-4" />
                </button>
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>

      {/* Back to top — appears after meaningful scroll, scrolls the parent
          <main> (the actual scroll container in this app shell). */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label={t("backToTop")}
        title={t("backToTop")}
        className={`fixed bottom-6 right-6 z-50 inline-flex items-center justify-center rounded-full border border-white/15 bg-fp-card/80 p-2.5 text-gray-300 backdrop-blur-sm hover:border-white/30 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
          showBackToTop ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <ArrowUpIcon className="size-5" />
      </button>
    </div>
  );
};

interface SectionHeaderProps {
  label: string;
  title: string;
  subtitle?: string;
}

const SectionHeader = ({ label, title, subtitle }: SectionHeaderProps) => (
  <div className="max-w-2xl">
    <span className="text-[10px] font-medium uppercase tracking-[0.24em] text-gray-500">
      {label}
    </span>
    <h2 className="mt-2 text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-3 text-sm leading-relaxed text-gray-400 sm:text-base">
        {subtitle}
      </p>
    )}
  </div>
);

export default Home;
